import Chip from "../Chip";
import { Icon } from "../Icon";
import { ModalHeader } from "../ModalHeader";
import { ModalBottom } from "../ModalBottom";
import { forwardRef, ReactNode, SyntheticEvent } from "react";
import { Locale } from "../../../../store/localeStore";
import {
  ANNOTATION_TYPE as ANNOTATION_TYPE_MAP,
  ValueOf,
} from "../../constants/constants";
import { TYPOGRAPHY_SCALE } from "../../constants/styles";
import { localeString } from "../../../shared/constants/locale";
import { Box, Paper, PaperProps, SxProps, Typography } from "@mui/material";
import { ListSelect } from "../ListSelect";
import DatePicker, { DatePickerProps } from "../DatePicker";

export interface ContainerProps extends PaperProps {
  children?: ReactNode;
  sx?: SxProps;
  onClose?: () => void;
  onApply?: () => void;
}

export const Container = forwardRef<HTMLDivElement, ContainerProps>(
  ({ children, sx, onClose, onApply, ...props }, ref) => {
    return (
      <Paper ref={ref} sx={{ ...paper, ...sx }} {...props}>
        <ModalHeader title="필터" size="small" onClose={onClose} />
        {children}
        <ModalBottom
          sx={{ borderTop: "1px solid #E0E0E0" }}
          leftButton={false}
          rightButtonText="적용"
          onRightButtonClick={onApply}
        />
      </Paper>
    );
  }
);

Container.displayName = "Container";

const paper = {
  width: "300px",
  maxWidth: "300px",
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
} as const;

export interface FilterContainerProps {
  children?: ReactNode;
  title: string;
  sx?: SxProps;
}

const FilterContainer = forwardRef<HTMLDivElement, FilterContainerProps>(
  ({ children, title, sx }, ref) => {
    return (
      <Box ref={ref} sx={{ ...filterContainer, ...sx }}>
        <Box sx={filterContainerHeader}>
          <Typography sx={TYPOGRAPHY_SCALE.subtitle2}>{title}</Typography>
        </Box>
        {children}
      </Box>
    );
  }
);

FilterContainer.displayName = "FilterContainer";

const filterContainer = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  padding: "16px",
} as const;

const filterContainerHeader = {
  height: 35,
  display: "flex",
  alignItems: "center",
} as const;

// 각 원소는 정본 상수(constants.tsx 에서 ANNOTATION_TYPE_MAP 으로 import 한 닫힌 집합)를
// 참조하지만 목록 자체는 손 열거다 — 배열 순서가 곧 칩 표시 순서라 명시 유지.
export const ISSUE_TYPE_CHIP_ORDER = [
  ANNOTATION_TYPE_MAP.marker,
  ANNOTATION_TYPE_MAP.line,
  ANNOTATION_TYPE_MAP.polygon,
  ANNOTATION_TYPE_MAP.rectangle,
  ANNOTATION_TYPE_MAP.text,
] as const;

export type IssueType = (typeof ISSUE_TYPE_CHIP_ORDER)[number];

// 칩 순서 배열이 정본을 전수 커버하는지 검사하는 컴파일 타임 가드.
// 정본(ANNOTATION_TYPE_MAP)에 종류가 추가되면 MissingFromChipOrder 가 never 가 아니게 되고
// AssertNever 제약을 위반해 컴파일 에러가 난다 — 그때는 위 ISSUE_TYPE_CHIP_ORDER 에도 그 종류를
// 추가하라. 즉 ALL_ISSUE_TYPES 의 "전수" 약속은 두 집합이 같을 때만 성립하고, 어긋나면 조용히
// 넘어가지 않는다.
type MissingFromChipOrder = Exclude<
  ValueOf<typeof ANNOTATION_TYPE_MAP>,
  IssueType
>;
type AssertNever<T extends never> = T;
type _ChipOrderCoversCanonical = AssertNever<MissingFromChipOrder>;

export const ALL_ISSUE_TYPES: IssueType[] = [...ISSUE_TYPE_CHIP_ORDER];

export interface TypeFilterProps {
  locale?: Locale["locale"];
  value: IssueType[];
  onChange: (value: IssueType[]) => void;
}

export const TypeFilter = forwardRef<HTMLDivElement, TypeFilterProps>(
  ({ locale = "ko", value = [], onChange }, ref) => {
    const onChipClick = (chipValue: IssueType) => {
      const newValue = value.includes(chipValue)
        ? value.filter((v) => v !== chipValue)
        : [...value, chipValue];
      onChange(newValue);
    };

    const getIcon = (type: IssueType) => {
      switch (type) {
        case ANNOTATION_TYPE_MAP.text:
          return "24/tool/basic/text";
        case ANNOTATION_TYPE_MAP.polygon:
          return "24/tool/basic/shape";
        case ANNOTATION_TYPE_MAP.rectangle:
          return "24/tool/basic/square";
        case ANNOTATION_TYPE_MAP.marker:
          return "24/tool/basic/pin";
        case ANNOTATION_TYPE_MAP.line:
          return "24/tool/basic/line";
        default:
          return "24/tool/basic/text";
      }
    };

    return (
      <FilterContainer
        ref={ref}
        title={"이슈 타입"}
        sx={{ paddingBottom: "0" }}
      >
        <Box sx={typeContainer}>
          {ISSUE_TYPE_CHIP_ORDER.map((type) => (
            <Chip
              key={type}
              sx={chip}
              label={localeString.constants[type][locale]}
              appearance={value.includes(type) ? "tag" : "default"}
              leftIcon={<Icon name={getIcon(type)} size={24} />}
              onClick={() => onChipClick(type)}
            />
          ))}
        </Box>
      </FilterContainer>
    );
  }
);

TypeFilter.displayName = "TypeFilter";

const typeContainer = {
  width: "100%",
  display: "flex",
  gap: "10px",
  alignSelf: "stretch",
  flexWrap: "wrap",
} as const;

const chip = {
  height: "32px",
  padding: "4px 12px",
} as const;

export interface CategoryFilterProps<T = string> {
  locale?: Locale["locale"];
  options: T[];
  value: T[];
  onChange: (value: T[]) => void;
}

export const CategoryFilter = <T = string,>({
  locale = "ko",
  options,
  value,
  onChange,
}: CategoryFilterProps<T> &
  Omit<React.HTMLAttributes<HTMLDivElement>, "onChange">) => {
  const onListSelectChange = (_e: SyntheticEvent, newValue: T[]) => {
    onChange(newValue);
  };

  const onAllSelect = (currentAllSelected: boolean) => {
    if (currentAllSelected) onChange([]);
    else onChange([...options]);
  };

  return (
    <FilterContainer title={"이슈 사항"} sx={{ paddingBottom: "0" }}>
      <Box sx={typeContainer}>
        <ListSelect
          multiple
          hasAllSelect={true}
          onAllSelect={onAllSelect}
          allSelected={value.length === options.length}
          options={options.map((opt) => String(opt))}
          value={value.map((val) => String(val))}
          onChange={onListSelectChange}
        />
      </Box>
    </FilterContainer>
  );
};

CategoryFilter.displayName = "CategoryFilter";

export interface DegreeFilterProps {
  locale?: Locale["locale"];
  value: DegreeType[];
  onChange: (value: DegreeType[]) => void;
}

export const DEGREE_TYPE = ["심각도", "긴급도", "중요도"] as const;

export type DegreeType = (typeof DEGREE_TYPE)[number];

export const ALL_DEGREE_TYPES: DegreeType[] = [...DEGREE_TYPE];

export const DegreeFilter = forwardRef<HTMLDivElement, DegreeFilterProps>(
  ({ locale = "ko", value = [], onChange }, ref) => {
    const onChipClick = (chipValue: DegreeType) => {
      const newValue = value.includes(chipValue)
        ? value.filter((v) => v !== chipValue)
        : [...value, chipValue];
      onChange(newValue);
    };

    const getLocaleKey = (type: DegreeType) => {
      switch (type) {
        case "심각도":
          return "severity";
        case "긴급도":
          return "urgenty";
        case "중요도":
          return "importance";
        default:
          return "severity";
      }
    };

    return (
      <FilterContainer
        title={"이슈 정도"}
        ref={ref}
        sx={{ paddingBottom: "0" }}
      >
        <Box sx={typeContainer}>
          {DEGREE_TYPE.map((type) => (
            <Chip
              key={type}
              sx={chip}
              label={localeString.constants[getLocaleKey(type)][locale]}
              appearance={value.includes(type) ? "tag" : "default"}
              onClick={() => onChipClick(type)}
            />
          ))}
        </Box>
      </FilterContainer>
    );
  }
);

DegreeFilter.displayName = "DegreeFilter";

export interface GradeFilterProps {
  value: GradeType[];
  onChange: (value: GradeType[]) => void;
}

export const GRADE_TYPE = [1, 2, 3, 4, 5] as const;

export type GradeType = (typeof GRADE_TYPE)[number];

export const ALL_GRADE_TYPES: GradeType[] = [...GRADE_TYPE];

export const GradeFilter = forwardRef<HTMLDivElement, GradeFilterProps>(
  ({ value = [], onChange }, ref) => {
    const onChipClick = (chipValue: GradeType) => {
      const newValue = value.includes(chipValue)
        ? value.filter((v) => v !== chipValue)
        : [...value, chipValue];
      onChange(newValue);
    };

    return (
      <FilterContainer
        title={"이슈 단계"}
        ref={ref}
        sx={{ paddingBottom: "0" }}
      >
        <Box sx={typeContainer}>
          {GRADE_TYPE.map((type) => (
            <Chip
              key={type}
              sx={chip}
              label={type}
              appearance={value.includes(type) ? "tag" : "default"}
              onClick={() => onChipClick(type)}
            />
          ))}
        </Box>
      </FilterContainer>
    );
  }
);

GradeFilter.displayName = "GradeFilter";

export interface DateFilterProps {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (startDate: Date | null, endDate: Date | null) => void;
  datePickerProps?: Omit<
    DatePickerProps,
    "startDate" | "endDate" | "onChange" | "ref"
  >;
}

export const DateFilter = forwardRef<HTMLDivElement, DateFilterProps>(
  ({ startDate, endDate, onChange, datePickerProps }, ref) => {
    return (
      <FilterContainer title={"조회 범위"} ref={ref}>
        <DatePicker
          startDate={startDate}
          endDate={endDate}
          onChange={onChange}
          {...datePickerProps}
        />
      </FilterContainer>
    );
  }
);

DateFilter.displayName = "DateFilter";

export const IssueFilter = {
  Container,
  TypeFilter,
  CategoryFilter,
  DegreeFilter,
  GradeFilter,
  DateFilter,
};

export default IssueFilter;
